const ODOMETER_SELECTOR = '.odometer';
const NUMBER_PATTERN = /^(\d[\d,]*)(.*)$/;
const DIGIT_INK_REFERENCE = '0';
const DIGIT_CHARACTERS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const CELL_LABELS = ['', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '1'];
const CELL_COUNT = CELL_LABELS.length;
const LAST_CELL_POSITION = CELL_COUNT - 1;
const ZERO_CELL_POSITION = 10;
const ENTER_LINE_FROM_VIEWPORT_BOTTOM = 0.12;
const LEAVE_CONFIRM_MS = 150;
const ROLL_FORWARD_MS = 1230;
const ROLL_STAGGER_PER_PLACE_MS = 230;
const MIN_COLUMN_ROLL_MS = 300;
const ROLL_BACKWARD_MS = 250;
const FADE_IN_MS = 200;
const FADE_OUT_DELAY_MS = 100;
const FADE_OUT_MS = 130;
const EDGE_FADE_EM = 0.2;
const EXPO_OUT_FLOOR = 1 - Math.pow(2, -10);
const VISUALLY_HIDDEN_STYLE = 'position:absolute;width:1px;height:1px;overflow:hidden;clip-path:inset(50%);white-space:nowrap;';

function isElementorEditor() {
    return document.body.classList.contains('elementor-editor-active');
}

function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function easeOutExpo(progress) {
    if (progress >= 1) {
        return 1;
    }

    return (1 - Math.pow(2, -10 * progress)) / EXPO_OUT_FLOOR;
}

function clampBetweenZeroAndOne(value) {
    return Math.min(1, Math.max(0, value));
}

function measureTextWidthInEm(element, text, fontSizePixels) {
    const probe = document.createElement('span');

    probe.style.cssText = 'display:inline-block;white-space:pre;';
    probe.textContent = text;
    element.appendChild(probe);

    const widthPixels = probe.getBoundingClientRect().width;

    element.removeChild(probe);

    return widthPixels / fontSizePixels;
}

function measureGlyphWidthsInEm(element, text, fontSizePixels) {
    const probe = document.createElement('span');

    probe.style.cssText = 'display:inline-block;white-space:pre;';
    probe.textContent = text;
    element.appendChild(probe);

    const textNode = probe.firstChild;
    const glyphWidthsInEm = text.split('').map((character, characterIndex) => {
        const glyphRange = document.createRange();

        glyphRange.setStart(textNode, characterIndex);
        glyphRange.setEnd(textNode, characterIndex + 1);

        return glyphRange.getBoundingClientRect().width / fontSizePixels;
    });

    element.removeChild(probe);

    return glyphWidthsInEm;
}

function measureWidestDigitInEm(element, fontSizePixels) {
    const digitWidthsInEm = DIGIT_CHARACTERS.map((digit) => measureTextWidthInEm(element, digit, fontSizePixels));

    return Math.max(...digitWidthsInEm);
}

function readGlyphGeometry(element) {
    const style = window.getComputedStyle(element);
    const fontSizePixels = parseFloat(style.fontSize);
    const canvasContext = document.createElement('canvas').getContext('2d');

    canvasContext.font = style.fontStyle + ' ' + style.fontWeight + ' ' + fontSizePixels + 'px ' + style.fontFamily;

    const digitMetrics = canvasContext.measureText(DIGIT_INK_REFERENCE);
    const inkAscentEm = digitMetrics.actualBoundingBoxAscent / fontSizePixels;
    const inkDescentEm = digitMetrics.actualBoundingBoxDescent / fontSizePixels;
    const fontAscentEm = digitMetrics.fontBoundingBoxAscent / fontSizePixels;
    const fontDescentEm = digitMetrics.fontBoundingBoxDescent / fontSizePixels;
    const baselineInsideEmptyLineEm = (fontAscentEm - fontDescentEm) / 2;

    return {
        fontSizePixels,
        windowHeightEm: inkAscentEm + inkDescentEm,
        inkDescentEm,
        baselineShiftEm: inkAscentEm - baselineInsideEmptyLineEm,
    };
}

function parseNumberAndSuffix(text) {
    const match = NUMBER_PATTERN.exec(text);

    if (match === null) {
        return null;
    }

    const numberText = match[1];
    const suffixText = match[2];
    const targetValue = Number(numberText.replace(/,/g, ''));

    if (!Number.isFinite(targetValue)) {
        return null;
    }

    return { numberText, suffixText, targetValue };
}

function createColumn(clipWidthEm, geometry) {
    const column = document.createElement('span');
    const strip = document.createElement('span');
    const cellHeightEm = geometry.windowHeightEm + EDGE_FADE_EM * 2;
    const edgeMask = 'linear-gradient(to bottom,transparent 0,#000 ' + EDGE_FADE_EM + 'em,#000 calc(100% - ' + EDGE_FADE_EM + 'em),transparent 100%)';

    column.style.cssText = 'display:inline-block;overflow:hidden;text-align:left;width:' + clipWidthEm + 'em;height:' + cellHeightEm + 'em;margin-top:-' + cellHeightEm + 'em;vertical-align:-' + (geometry.inkDescentEm + EDGE_FADE_EM) + 'em;-webkit-mask-image:' + edgeMask + ';mask-image:' + edgeMask + ';';
    strip.style.cssText = 'display:block;';

    CELL_LABELS.forEach((label) => {
        const cell = document.createElement('span');

        cell.style.cssText = 'display:block;position:relative;line-height:0;height:' + cellHeightEm + 'em;top:' + (geometry.baselineShiftEm + EDGE_FADE_EM) + 'em;';
        cell.textContent = label;
        strip.appendChild(cell);
    });

    column.appendChild(strip);

    return { column, strip };
}

function createSeparator(separatorText) {
    const separator = document.createElement('span');

    separator.style.cssText = 'display:inline-block;overflow:visible;white-space:pre;';
    separator.textContent = separatorText;

    return separator;
}

function targetPositionOfDigit(digit) {
    return digit === 0 ? ZERO_CELL_POSITION : digit;
}

function buildStage(element, numberText, geometry) {
    const stage = document.createElement('span');
    const columns = [];
    const separators = [];
    const glyphWidthsInEm = measureGlyphWidthsInEm(element, numberText, geometry.fontSizePixels);
    const widestDigitInEm = measureWidestDigitInEm(element, geometry.fontSizePixels);
    const digitCount = numberText.replace(/,/g, '').length;
    let digitsToTheRight = digitCount;

    stage.setAttribute('aria-hidden', 'true');
    stage.style.cssText = 'display:inline-block;white-space:nowrap;';

    numberText.split('').forEach((character, characterIndex) => {
        const characterWidthEm = glyphWidthsInEm[characterIndex];

        if (character === ',') {
            const separator = createSeparator(character);

            separators.push({ element: separator, widthEm: characterWidthEm, leftColumnIndex: columns.length - 1 });
            stage.appendChild(separator);

            return;
        }

        digitsToTheRight -= 1;

        const clipWidthEm = Math.max(widestDigitInEm, characterWidthEm);
        const built = createColumn(clipWidthEm, geometry);

        columns.push({
            column: built.column,
            strip: built.strip,
            widthEm: characterWidthEm,
            clipWidthEm,
            power: digitsToTheRight,
            targetPosition: targetPositionOfDigit(Number(character)),
            position: 0,
            fromPosition: 0,
            toPosition: 0,
            delayMs: 0,
            durationMs: 0,
            startedAt: 0,
        });
        stage.appendChild(built.column);
    });

    return { stage, columns, separators };
}

function openness(column) {
    return clampBetweenZeroAndOne(column.position);
}

function renderColumn(column) {
    const cellPosition = Math.min(column.position, LAST_CELL_POSITION);
    const offsetPercent = (cellPosition / CELL_COUNT) * 100;
    const columnOpenness = openness(column);

    column.strip.style.transform = 'translateY(-' + offsetPercent + '%)';
    column.column.style.marginRight = column.widthEm * columnOpenness - column.clipWidthEm + 'em';
    column.column.style.opacity = columnOpenness === 1 ? '' : String(columnOpenness);
}

function renderSeparator(separator, columns) {
    const leftColumn = columns[separator.leftColumnIndex];
    const leftOpenness = openness(leftColumn);

    separator.element.style.width = separator.widthEm * leftOpenness + 'em';
    separator.element.style.opacity = leftOpenness === 1 ? '' : String(leftOpenness);
}

function renderOdometer(odometer) {
    odometer.columns.forEach(renderColumn);
    odometer.separators.forEach((separator) => renderSeparator(separator, odometer.columns));
}

function stepColumn(column, timestamp) {
    const elapsedMs = timestamp - column.startedAt - column.delayMs;

    if (elapsedMs < 0) {
        return true;
    }

    const rollProgress = clampBetweenZeroAndOne(elapsedMs / column.durationMs);
    const easedProgress = easeOutExpo(rollProgress);

    column.position = column.fromPosition + (column.toPosition - column.fromPosition) * easedProgress;

    return rollProgress < 1;
}

function stepOdometer(odometer, timestamp) {
    const stillRollingFlags = odometer.columns.map((column) => stepColumn(column, timestamp));
    const isStillRolling = stillRollingFlags.some((isRolling) => isRolling);

    renderOdometer(odometer);

    if (isStillRolling) {
        odometer.frameRequest = window.requestAnimationFrame((nextTimestamp) => stepOdometer(odometer, nextTimestamp));
    } else {
        odometer.frameRequest = 0;
    }
}

function startRolling(odometer) {
    window.cancelAnimationFrame(odometer.frameRequest);
    odometer.frameRequest = window.requestAnimationFrame((timestamp) => {
        odometer.columns.forEach((column) => {
            column.startedAt = timestamp;
        });
        stepOdometer(odometer, timestamp);
    });
}

function planRoll(odometer, isForward) {
    odometer.columns.forEach((column) => {
        const delayMs = isForward ? column.power * ROLL_STAGGER_PER_PLACE_MS : 0;
        const durationMs = isForward ? Math.max(MIN_COLUMN_ROLL_MS, ROLL_FORWARD_MS - delayMs) : ROLL_BACKWARD_MS;

        column.fromPosition = column.position;
        column.toPosition = isForward ? column.targetPosition : 0;
        column.delayMs = delayMs;
        column.durationMs = durationMs;
    });
}

function fade(targets, toOpacity, durationMs, delayMs) {
    targets.forEach((target) => {
        target.getAnimations().forEach((animation) => animation.cancel());
        target.style.opacity = String(toOpacity);
        target.animate(
            [{ opacity: toOpacity === 1 ? 0 : 1 }, { opacity: toOpacity }],
            { duration: durationMs, delay: delayMs, easing: 'ease-out', fill: 'backwards' }
        );
    });
}

function rollForward(odometer) {
    odometer.isShown = true;
    planRoll(odometer, true);
    startRolling(odometer);
    fade(odometer.fadeTargets, 1, FADE_IN_MS, 0);
}

function rollBackward(odometer) {
    odometer.isShown = false;
    planRoll(odometer, false);
    startRolling(odometer);
    fade(odometer.fadeTargets, 0, FADE_OUT_MS, FADE_OUT_DELAY_MS);
}

function fadeTargetsOf(element) {
    const figure = element.parentElement;
    const item = figure === null ? null : figure.parentElement;

    if (item === null) {
        return [element];
    }

    return Array.from(item.children);
}

function hasEnteredViewport(odometer) {
    const viewportHeight = window.innerHeight;
    const elementBox = odometer.element.getBoundingClientRect();
    const hasPassedEnterLine = elementBox.top < viewportHeight * (1 - ENTER_LINE_FROM_VIEWPORT_BOTTOM);
    const isNotAboveViewport = elementBox.bottom > 0;

    return hasPassedEnterLine && isNotAboveViewport;
}

function hasLeftViewport(odometer) {
    const viewportHeight = window.innerHeight;
    const elementBox = odometer.element.getBoundingClientRect();
    const isBelowViewport = elementBox.top >= viewportHeight;
    const isAboveViewport = elementBox.bottom <= 0;

    return isBelowViewport || isAboveViewport;
}

function followScroll(odometer) {
    if (hasEnteredViewport(odometer)) {
        window.clearTimeout(odometer.leaveTimer);
        odometer.leaveTimer = 0;

        if (!odometer.isShown) {
            rollForward(odometer);
        }

        return;
    }

    if (odometer.isShown && odometer.leaveTimer === 0 && hasLeftViewport(odometer)) {
        odometer.leaveTimer = window.setTimeout(() => {
            odometer.leaveTimer = 0;

            if (odometer.isShown && hasLeftViewport(odometer)) {
                rollBackward(odometer);
            }
        }, LEAVE_CONFIRM_MS);
    }
}

function createOdometer(element) {
    const originalText = element.textContent.trim();
    const parsed = parseNumberAndSuffix(originalText);

    if (parsed === null) {
        return null;
    }

    const built = buildStage(element, parsed.numberText, readGlyphGeometry(element));
    const accessibleText = document.createElement('span');
    const suffix = document.createElement('span');
    const fadeTargets = fadeTargetsOf(element);

    accessibleText.style.cssText = VISUALLY_HIDDEN_STYLE;
    accessibleText.textContent = originalText;
    suffix.setAttribute('aria-hidden', 'true');
    suffix.textContent = parsed.suffixText;

    element.textContent = '';
    element.appendChild(accessibleText);
    element.appendChild(built.stage);
    element.appendChild(suffix);
    fadeTargets.forEach((target) => {
        target.style.opacity = '0';
    });

    const odometer = {
        element,
        fadeTargets,
        columns: built.columns,
        separators: built.separators,
        isShown: false,
        frameRequest: 0,
        leaveTimer: 0,
    };

    renderOdometer(odometer);

    return odometer;
}

function startOdometers() {
    if (isElementorEditor() || prefersReducedMotion()) {
        return;
    }

    document.fonts.ready.then(() => {
        const odometers = Array.from(document.querySelectorAll(ODOMETER_SELECTOR))
            .map(createOdometer)
            .filter((odometer) => odometer !== null);
        let frameRequest = 0;

        function checkAll() {
            frameRequest = 0;
            odometers.forEach(followScroll);
        }

        function scheduleCheck() {
            if (frameRequest === 0) {
                frameRequest = window.requestAnimationFrame(checkAll);
            }
        }

        window.addEventListener('scroll', scheduleCheck, { passive: true });
        window.addEventListener('resize', scheduleCheck);
        checkAll();
    });
}

startOdometers();
